import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ChatConversation, ChatMessage, FileAttachment } from '../types/chat';
import chatService from '../services/chatService';

export interface ArtifactLimitStatus {
  limitReached: boolean;
  currentCount: number;
  limit: number;
  upgradeRequired: boolean;
}

interface ChatContextType {
  conversations: ChatConversation[];
  currentConversation: ChatConversation | null;
  loading: boolean;
  error: string | null;
  streamingMessageId: string | null;
  createConversation: (prompt: string, templateId?: string) => Promise<string>;
  createConversationStream: (prompt: string, templateId?: string, attachments?: FileAttachment[], templateMetadata?: { templateTitle?: string; helperPrompt?: string; initialPrompt?: string }, projectId?: string, signal?: AbortSignal, onArtifactLimitStatus?: (status: ArtifactLimitStatus) => void) => Promise<string>;
  createTemplateConversation: (userPrompt: string, helperPrompt: string, templateId?: string) => Promise<string>;
  sendMessage: (conversationId: string, message: string) => Promise<void>;
  sendMessageStream: (conversationId: string, message: string, attachments?: FileAttachment[], signal?: AbortSignal, onArtifactLimitStatus?: (status: ArtifactLimitStatus) => void) => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadUserConversations: () => Promise<void>;
  updateConversation: (conversationId: string, title: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  cancelStreaming: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ChatConversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

  // Helper function to add conversation with deduplication
  const addConversationWithDedup = useCallback((newConversation: ChatConversation) => {
    setConversations(prev => {
      // Check if conversation already exists
      const exists = prev.some(c => c.id === newConversation.id);
      if (exists) {
        return prev;
      }
      return [newConversation, ...prev];
    });
  }, []);

  const createConversation = useCallback(async (prompt: string, templateId?: string): Promise<string> => {
    setLoading(true);
    setError(null);
    try {
      const response = await chatService.createConversation(prompt, templateId);
      
      const initialConversation: ChatConversation = {
        id: response.conversationId,
        userId: '', // Will be set by backend
        title: prompt.length > 50 ? prompt.substring(0, 47) + '...' : prompt,
        messages: [
          {
            id: Date.now().toString(),
            role: 'user',
            content: prompt,
            timestamp: new Date()
          },
          response.message
        ],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setCurrentConversation(initialConversation);
      addConversationWithDedup(initialConversation);
      
      try {
        const fullConversation = await chatService.getConversation(response.conversationId);
        if (fullConversation) {
          fullConversation.messages = fullConversation.messages || [];
          setCurrentConversation(fullConversation);
          setConversations(prev => prev.map(c => 
            c.id === fullConversation.id ? fullConversation : c
          ));
        }
      } catch (error) {
        console.error('Failed to fetch full conversation:', error);
      }
      
      return response.conversationId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addConversationWithDedup]);

  const createTemplateConversation = useCallback(async (userPrompt: string, helperPrompt: string, templateId?: string): Promise<string> => {
    setLoading(true);
    setError(null);
    try {
      const conversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const initialConversation: ChatConversation = {
        id: conversationId,
        userId: '', // Will be set when saved
        title: userPrompt.length > 50 ? userPrompt.substring(0, 47) + '...' : userPrompt,
        messages: [
          {
            id: `msg_${Date.now()}_1`,
            role: 'user',
            content: userPrompt,
            timestamp: new Date()
          },
          {
            id: `msg_${Date.now()}_2`,
            role: 'assistant',
            content: helperPrompt,
            timestamp: new Date()
          }
        ],
        createdAt: new Date(),
        updatedAt: new Date(),
        templateId: templateId
      };
      
      setCurrentConversation(initialConversation);
      addConversationWithDedup(initialConversation);
      
      return conversationId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template conversation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addConversationWithDedup]);

  const sendMessage = useCallback(async (conversationId: string, message: string) => {
    setLoading(true);
    setError(null);
    try {
      if (currentConversation && 
          currentConversation.id === conversationId && 
          conversationId.startsWith('conv_')) {

        const fullPrompt = currentConversation.messages
          .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
          .join('\n\n') + `\n\nUser: ${message}`;
        
        const response = await chatService.createConversation(fullPrompt, currentConversation.templateId);
        
        const updatedConversation: ChatConversation = {
          ...currentConversation,
          id: response.conversationId,
          messages: [
            ...currentConversation.messages,
            {
              id: Date.now().toString(),
              role: 'user',
              content: message,
              timestamp: new Date()
            },
            response.message
          ],
          updatedAt: new Date()
        };
        
        setCurrentConversation(updatedConversation);
        setConversations(prev => prev.map(c => 
          c.id === conversationId ? updatedConversation : c
        ));
        
        window.history.replaceState(null, '', `/chat/${response.conversationId}`);
      } else {
        const response = await chatService.sendMessage(conversationId, message);
        
        if (currentConversation && currentConversation.id === conversationId) {
          const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: message,
            timestamp: new Date()
          };
          
          setCurrentConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...prev.messages, userMessage, response.message],
              updatedAt: new Date()
            };
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentConversation]);

  const loadConversation = useCallback(async (conversationId: string) => {
    setLoading(true);
    setError(null);
    try {
      const conversation = await chatService.getConversation(conversationId);
      if (conversation) {
        conversation.messages = conversation.messages || [];
      }
      setCurrentConversation(conversation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userConversations = await chatService.getUserConversations();

      // Deduplicate conversations by ID
      const uniqueConversations = userConversations.reduce((acc, conv) => {
        if (!acc.find(c => c.id === conv.id)) {
          acc.push(conv);
        }
        return acc;
      }, [] as ChatConversation[]);

      setConversations(uniqueConversations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConversation = useCallback(async (conversationId: string, title: string) => {
    setError(null);
    try {
      await chatService.updateConversation(conversationId, title);
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, title } : c
      ));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(prev => prev ? { ...prev, title } : null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update conversation');
      throw err;
    }
  }, [currentConversation]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    setLoading(true);
    setError(null);
    try {
      await chatService.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentConversation]);

  const createConversationStream = useCallback(async (
    prompt: string,
    templateId?: string,
    attachments?: FileAttachment[],
    templateMetadata?: {
      templateTitle?: string;
      helperPrompt?: string;
      initialPrompt?: string;
    },
    projectId?: string,
    signal?: AbortSignal,
    onArtifactLimitStatus?: (status: ArtifactLimitStatus) => void
  ): Promise<string> => {
    setLoading(true);
    setError(null);
    let conversationIdRef = '';
    let assistantMessageIdRef = '';

    try {
      const { conversationId } = await chatService.createConversationStream(
        prompt,
        templateId,
        (chunk: string) => {
          setCurrentConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: prev.messages.map(msg =>
                msg.id === assistantMessageIdRef
                  ? { ...msg, content: msg.content + chunk }
                  : msg
              )
            };
          });
        },
        (msgId: string) => {
          assistantMessageIdRef = msgId;
          setStreamingMessageId(msgId);
          const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: prompt,
            timestamp: new Date(),
            attachments: attachments
          };
          const assistantMessage: ChatMessage = {
            id: msgId,
            role: 'assistant',
            content: '',
            timestamp: new Date()
          };

          const newConversation: ChatConversation = {
            id: conversationIdRef,
            userId: '',
            title: prompt.length > 50 ? prompt.substring(0, 47) + '...' : prompt,
            messages: [userMessage, assistantMessage],
            templateMetadata: templateMetadata,
            projectId: projectId,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          setCurrentConversation(newConversation);
          addConversationWithDedup(newConversation);
        },
        (convId: string) => {
          conversationIdRef = convId;
        },
        attachments,
        templateMetadata,
        projectId,
        signal,
        onArtifactLimitStatus,
        () => {
          setCurrentConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: prev.messages.map(msg =>
                msg.id === assistantMessageIdRef ? { ...msg, truncated: true } : msg
              )
            };
          });
        }
      );

      setStreamingMessageId(null);
      return conversationId;
    } catch (err) {
      setStreamingMessageId(null);
      // Don't set error for aborted requests
      const isAbortError = (err instanceof DOMException && err.name === 'AbortError') ||
                           (err instanceof Error && err.name === 'AbortError');

      // Check if this is a limit error or grace period error (handled by Chat component)
      const isLimitError = (err as any)?.code === 'MESSAGE_LIMIT_REACHED' || (err as any)?.code === 'ARTIFACT_LIMIT_REACHED';
      const isGracePeriodError = (err as any)?.code === 'GRACE_PERIOD_ACTIVE';

      if (isAbortError) {
        // Add "Conversation stopped" system message
        setCurrentConversation(prev => {
          if (!prev) return prev;

          // Add a system message indicating the conversation was stopped
          const systemMessage: ChatMessage = {
            id: `system-${Date.now()}`,
            role: 'assistant',
            content: '_Conversation stopped_',
            timestamp: new Date()
          };

          return {
            ...prev,
            messages: [...prev.messages, systemMessage],
            updatedAt: new Date()
          };
        });
      } else if (!isLimitError && !isGracePeriodError) {
        // Don't set error for limit errors or grace period errors - they're handled in Chat component
        setError(err instanceof Error ? err.message : 'Failed to create conversation');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addConversationWithDedup]);

  const sendMessageStream = useCallback(async (conversationId: string, message: string, attachments?: FileAttachment[], signal?: AbortSignal, onArtifactLimitStatus?: (status: ArtifactLimitStatus) => void) => {
    setLoading(true);
    setError(null);
    try {
      await chatService.sendMessageStream(
        conversationId,
        message,
        (chunk: string, assistantMessageId: string) => {
          setCurrentConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: prev.messages.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: msg.content + chunk }
                  : msg
              ),
              updatedAt: new Date()
            };
          });
        },
        (userMessageId: string, assistantMessageId: string) => {
          setStreamingMessageId(assistantMessageId);
          setCurrentConversation(prev => {
            if (!prev) return prev;

            const userMessage: ChatMessage = {
              id: userMessageId,
              role: 'user',
              content: message,
              timestamp: new Date(),
              attachments: attachments
            };
            const assistantMessage: ChatMessage = {
              id: assistantMessageId,
              role: 'assistant',
              content: '',
              timestamp: new Date()
            };

            return {
              ...prev,
              messages: [...prev.messages, userMessage, assistantMessage],
              updatedAt: new Date()
            };
          });
        },
        attachments,
        signal,
        onArtifactLimitStatus,
        (_reason: string, assistantMessageId: string) => {
          setCurrentConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: prev.messages.map(msg =>
                msg.id === assistantMessageId ? { ...msg, truncated: true } : msg
              )
            };
          });
        }
      );

      setStreamingMessageId(null);
    } catch (err) {
      setStreamingMessageId(null);
      // Don't set error for aborted requests
      const isAbortError = (err instanceof DOMException && err.name === 'AbortError') ||
                           (err instanceof Error && err.name === 'AbortError');

      // Check if this is a limit error or grace period error (handled by Chat component)
      const isLimitError = (err as any)?.code === 'MESSAGE_LIMIT_REACHED' || (err as any)?.code === 'ARTIFACT_LIMIT_REACHED';
      const isGracePeriodError = (err as any)?.code === 'GRACE_PERIOD_ACTIVE';

      if (isAbortError) {
        // Add "Conversation stopped" system message
        setCurrentConversation(prev => {
          if (!prev) return prev;

          // Add a system message indicating the conversation was stopped
          const systemMessage: ChatMessage = {
            id: `system-${Date.now()}`,
            role: 'assistant',
            content: '_Conversation stopped_',
            timestamp: new Date()
          };

          return {
            ...prev,
            messages: [...prev.messages, systemMessage],
            updatedAt: new Date()
          };
        });
      } else if (!isLimitError && !isGracePeriodError) {
        // Don't set error for limit errors or grace period errors - they're handled in Chat component
        setError(err instanceof Error ? err.message : 'Failed to send message');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelStreaming = useCallback(() => {
    setStreamingMessageId(null);
  }, []);

  const value: ChatContextType = {
    conversations,
    currentConversation,
    loading,
    error,
    streamingMessageId,
    createConversation,
    createConversationStream,
    createTemplateConversation,
    sendMessage,
    sendMessageStream,
    loadConversation,
    loadUserConversations,
    updateConversation,
    deleteConversation,
    cancelStreaming
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};